// @ts-nocheck

import React, { useContext, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import SpinePlayer from '../../../../components/SpinePlayer';
import Formatter from '../../../../i18n/formatter';
import { getCurrency } from '../../../../i18n/l10n';
import theme from '../../../../styled-components/dark';
import { KLSpineAnimationIdsByBadgeIdAndStatus } from '../../constants/animations';
import { KLCollectibleLabelsByCollectibleId } from '../../constants/labels';
import { KLSoundIdsByBadgeIdAndStatus } from '../../constants/sounds';
import KLCollectibleBetslipDetailsCardContainer from '../../containers/KLOrientationStepDetailsCardContainer';
import {
  type KLCollectibleId,
  KLCollectibleIds,
  type KLCollectibleStatus,
  KLCollectibleStatuses,
} from '../../types/collectible';

import { KLViewContext } from './KLViewWrapper';

const ContentWrapper = styled.div`
  width: 100%;
  height: 66%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  text-transform: uppercase;
  font-weight: bold;
  font-size: 2.5em;
  padding: 0.5em 0 0 0;
`;

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Content = styled.div`
  font-weight: 600;
  font-size: 1.5em;
  text-align: center;
  line-height: 1.3em;

  opacity: 0;
  animation: ${fadeInAnimation} 0.7s ease 0.8s;
  animation-fill-mode: forwards;

  b {
    text-transform: uppercase;
    color: ${theme.KLNotification.highlightColor};
  }
`;

const SpinePlayerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  padding-top: 1em;
  margin: 1em 0;
  width: 100%;

  ${({ $height }) => css`
    height: ${$height}em;
  `}

  ${({ $isACollar }) =>
    $isACollar
      ? css`
          & canvas {
            transform: scale(0.7);
          }
        `
      : undefined}
`;

const WinningAmount = styled.div`
  font-weight: 600;
  font-size: 1.5em;
  text-align: center;

  b {
    color: ${theme.KLNotification.highlightColor};
  }
`;

const SmallText = styled.div`
  font-size: 0.8em;
`;

const BetslipDetailsWrapper = styled.div`
  margin-bottom: 3em;
  width: 100%;
  height: calc(34% - 3em);
`;

type KLNotificationCollectibleProps = {
  collectibleId: KLCollectibleId;
  collectibleStatus: KLCollectibleStatus;
  remainingKnifes?: number;
  amount?: number;
  betslipReference: string;
};

const KLNotificationCollectible = ({
  collectibleId,
  collectibleStatus,
  remainingKnifes,
  amount,
  betslipReference,
}: KLNotificationCollectibleProps) => {
  const { height: availableHeight } = useContext(KLViewContext);
  const animationRatio = useMemo(() => availableHeight / 745, [availableHeight]);

  const notificationBadgeTitle = useMemo(() => {
    if (collectibleStatus === KLCollectibleStatuses.WON) {
      return 'Bravo !';
    }
    if (collectibleStatus === KLCollectibleStatuses.LOST) {
      return 'Raté';
    }
    if (collectibleStatus === KLCollectibleStatuses.MISS) {
      if (remainingKnifes === 0) {
        return 'Éliminé';
      } else {
        return "Rien n'est perdu";
      }
    }
  }, [collectibleStatus, remainingKnifes]);

  const isABadge = useMemo(() => {
    const badges = [
      KLCollectibleIds.FISHING,
      KLCollectibleIds.FIRECAMP,
      KLCollectibleIds.HUT,
      KLCollectibleIds.RICE,
      KLCollectibleIds.WATER,
      KLCollectibleIds.BALANCE,
      KLCollectibleIds.GRAND_BLUE,
      KLCollectibleIds.DUO,
      KLCollectibleIds.SLOTH,
      KLCollectibleIds.BAMBOOS,
    ];

    return badges.includes(collectibleId);
  }, [collectibleId]);

  const isAComfort = useMemo(() => {
    const comforts = [KLCollectibleIds.CHOCOLATE, KLCollectibleIds.HOTEL, KLCollectibleIds.PIZZA];

    return comforts.includes(collectibleId);
  }, [collectibleId]);

  const isACollar = useMemo(() => collectibleId === KLCollectibleIds.COLLAR, [collectibleId]);
  const isATag = useMemo(() => collectibleId === KLCollectibleIds.TAG, [collectibleId]);
  const isAKnife = useMemo(() => collectibleId === KLCollectibleIds.KNIFE, [collectibleId]);
  const isOrientation = useMemo(
    () => new Set([KLCollectibleIds.MARK, KLCollectibleIds.TAG, KLCollectibleIds.KNIFE]).has(collectibleId),
    [collectibleId],
  );

  const collectibleNb = useMemo(() => {
    if (!isOrientation) {
      return;
    }

    if (
      collectibleId === KLCollectibleIds.KNIFE &&
      collectibleStatus === KLCollectibleStatuses.MISS &&
      typeof remainingKnifes === 'number'
    ) {
      return Math.abs(remainingKnifes - 2) % 3;
    }

    return remainingKnifes ? Math.abs(remainingKnifes - 3) % 3 : undefined;
  }, [remainingKnifes, collectibleId, isOrientation, collectibleStatus]);

  const notificationBadgeContent = useMemo(() => {
    if (isABadge) {
      if (collectibleStatus === KLCollectibleStatuses.WON) {
        return (
          <>
            Vous remportez <br /> le badge <b>{KLCollectibleLabelsByCollectibleId[collectibleId]?.collectibleLabel}</b>
          </>
        );
      } else if (collectibleStatus === KLCollectibleStatuses.LOST) {
        return (
          <>
            Vous n&apos;avez pas remporté <br /> le badge{' '}
            <b>{KLCollectibleLabelsByCollectibleId[collectibleId]?.collectibleLabel}</b>
          </>
        );
      }
    } else if (isAComfort) {
      if (collectibleStatus === KLCollectibleStatuses.WON) {
        return (
          <>
            Vous remportez <br /> l&apos;épreuve de confort{' '}
            <b>{KLCollectibleLabelsByCollectibleId[collectibleId].collectibleLabel}</b>
          </>
        );
      } else if (collectibleStatus === KLCollectibleStatuses.LOST) {
        return (
          <>
            Vous ne remportez pas <br /> l&apos;épreuve de confort{' '}
            <b>{KLCollectibleLabelsByCollectibleId[collectibleId].collectibleLabel}</b>
          </>
        );
      }
    } else if (isACollar) {
      if (collectibleStatus === KLCollectibleStatuses.WON) {
        return (
          <>
            Vous avez trouvé <br /> un <b>collier</b>
          </>
        );
      } else if (collectibleStatus === KLCollectibleStatuses.LOST) {
        return (
          <>
            Vous n&apos;avez pas trouvé <br /> de <b>collier</b>
          </>
        );
      }
    } else if (isOrientation) {
      if (collectibleStatus === KLCollectibleStatuses.WON) {
        return (
          <>
            Vous avez trouvé <br /> {isATag ? 'la ' : 'le '}
            <b>{KLCollectibleLabelsByCollectibleId[collectibleId]?.collectibleLabel}</b>
          </>
        );
      } else {
        if (isAKnife && collectibleStatus === KLCollectibleStatuses.MISS && collectibleNb !== undefined) {
          const knifeTexts = [
            <>
              Le premier <b>poignard</b> vient d&apos;être trouvé. <br /> Partez à la recherche du prochain repère.
            </>,
            <>
              Le deuxième <b>poignard</b> vient d&apos;être trouvé. <br /> Partez à la recherche du dernier repère.
            </>,
            <>
              Le dernier <b>poignard</b> vient d&apos;être trouvé. <br /> Vous êtes malheureusement éliminé.
            </>,
          ];
          return <SmallText>{knifeTexts[collectibleNb]}</SmallText>;
        }
        return (
          <>
            Vous n&apos;avez pas trouvé <br /> {isATag ? 'la ' : 'le '}
            <b>{KLCollectibleLabelsByCollectibleId[collectibleId]?.collectibleLabel}</b>
          </>
        );
      }
    }
  }, [
    isABadge,
    isAComfort,
    isACollar,
    isATag,
    isAKnife,
    isOrientation,
    collectibleId,
    collectibleNb,
    collectibleStatus,
  ]);

  const notificationBadgeWinningAmount = useMemo(() => {
    if (collectibleStatus === KLCollectibleStatuses.WON) {
      switch (collectibleId) {
        case KLCollectibleIds.CHOCOLATE:
        case KLCollectibleIds.HOTEL:
        case KLCollectibleIds.PIZZA: {
          const currency = getCurrency();
          const displayedAmount = Formatter.formatBankroll(amount, currency);
          return (
            <>
              <b>{displayedAmount}</b> en Freebets
            </>
          );
        }
        default:
          return undefined;
      }
    }
  }, [collectibleId, collectibleStatus, amount]);

  return (
    <>
      <ContentWrapper>
        <Title>{notificationBadgeTitle}</Title>
        <Content>{notificationBadgeContent}</Content>
        <SpinePlayerWrapper $isACollar={isACollar} $height={12 * animationRatio}>
          <SpinePlayer
            animationId={
              collectibleNb !== undefined
                ? KLSpineAnimationIdsByBadgeIdAndStatus[collectibleId]?.[collectibleNb]?.[collectibleStatus]
                : KLSpineAnimationIdsByBadgeIdAndStatus[collectibleId]?.[collectibleStatus]
            }
            soundId={
              collectibleNb !== undefined
                ? KLSoundIdsByBadgeIdAndStatus[collectibleId]?.[collectibleNb]?.[collectibleStatus]
                : KLSoundIdsByBadgeIdAndStatus[collectibleId]?.[collectibleStatus]
            }
            scale={0.3 * animationRatio}
          />
        </SpinePlayerWrapper>
        <WinningAmount>
          {notificationBadgeWinningAmount ? <>Gains : {notificationBadgeWinningAmount}</> : null}
        </WinningAmount>
      </ContentWrapper>
      <BetslipDetailsWrapper>
        <KLCollectibleBetslipDetailsCardContainer betslipReference={betslipReference} status={collectibleStatus} />
      </BetslipDetailsWrapper>
    </>
  );
};

export default KLNotificationCollectible;
